import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function DatenschutzPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  // Update navigation background on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    // Cleanup on unmount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ensure the page starts at the top when navigated to
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add noindex meta tag
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);

    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-lg' : 'bg-[#2F4858]'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <Link to="/" className="cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/535cd5cb202604744f94f7b2225e7be8/507ad711238d1ff66498f69658d73c1b.png"
                  alt="Hommen Consulting Logo"
                  className="h-12 w-auto"
                />
              </Link>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-[#2F4858] hover:text-[#FF6B35]' : 'text-white hover:text-[#FF6B35]'
                }`}
              >
                Startseite
              </Link>
              <Link
                to="/#services"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'text-[#2F4858] hover:text-[#FF6B35]' : 'text-white hover:text-[#FF6B35]'
                }`}
              >
                Leistungen
              </Link>
              <Link
                to="/#contact"
                className="px-6 py-2.5 bg-[#FF6B35] text-white text-sm font-medium rounded-full hover:bg-[#e55a2a] transition-colors whitespace-nowrap cursor-pointer"
              >
                Kontakt
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#2F4858] to-[#3a5a6e]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Datenschutzerklärung</h1>
          <p className="text-white/80 text-lg">Informationen zum Schutz Ihrer personenbezogenen Daten</p>
        </div>
      </section>

      {/* Content */}
      <main className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="space-y-12">
            {/* Responsible Party */}
            <article className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#2F4858]">
                  <i className="ri-shield-user-line text-2xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-[#2F4858] ml-4">1. Verantwortlicher</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-2 text-[15px]">
                <p>Verantwortlich für die Datenverarbeitung auf dieser Website ist:</p>
                <p className="font-semibold text-[#2F4858] mt-3">Hommen Consulting GmbH</p>
                <p>Westring 32</p>
                <p>41379 Brüggen</p>
                <p>Deutschland</p>
                <p className="mt-3">
                  <span className="font-medium text-[#2F4858]">Geschäftsführer:</span> Sven Firnrohr
                </p>
                <p>
                  <span className="font-medium text-[#2F4858]">E-Mail:</span>{' '}
                  <a
                    href="mailto:service@hommen-consulting.de"
                    className="hover:text-[#FF6B35] transition-colors cursor-pointer"
                  >
                    service@hommen-consulting.de
                  </a>
                </p>
                <p>
                  <span className="font-medium text-[#2F4858]">Telefon:</span>{' '}
                  <a href="tel:+4917631669010" className="hover:text-[#FF6B35] transition-colors cursor-pointer">
                    +49 (176) 316 69 010
                  </a>
                </p>
              </div>
            </article>

            {/* Overview */}
            <article className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#2F4858]">
                  <i className="ri-file-list-3-line text-2xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-[#2F4858] ml-4">2. Übersicht der Datenverarbeitungen</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 text-[15px]">
                <p>
                  Die nachfolgende Übersicht fasst die Arten der verarbeiteten Daten und die Zwecke ihrer Verarbeitung
                  zusammen und verweist auf die betroffenen Personen.
                </p>
                <div>
                  <p className="font-medium text-[#2F4858] mb-2">Arten der verarbeiteten Daten:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Kontaktdaten (z.B. E-Mail, Telefonnummern)</li>
                    <li>Inhaltsdaten (z.B. Eingaben in Formularen)</li>
                    <li>Nutzungsdaten (z.B. besuchte Seiten, Zugriffszeit)</li>
                    <li>Meta-/Kommunikationsdaten (z.B. Geräte-Informationen, IP-Adressen)</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-[#2F4858] mb-2">Kategorien betroffener Personen:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Nutzer der Website</li>
                    <li>Kommunikationspartner</li>
                    <li>Interessenten und Geschäftspartner</li>
                  </ul>
                </div>
              </div>
            </article>

            {/* Legal Basis */}
            <article className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#2F4858]">
                  <i className="ri-scales-3-line text-2xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-[#2F4858] ml-4">3. Rechtsgrundlagen</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 text-[15px]">
                <p>
                  Im Folgenden erhalten Sie eine Übersicht der Rechtsgrundlagen der DSGVO, auf deren Basis wir
                  personenbezogene Daten verarbeiten:
                </p>
                <ul className="space-y-3 ml-2">
                  <li>
                    <strong className="text-[#2F4858]">
                      Einwilligung (Art. 6 Abs. 1 S. 1 lit. a DSGVO)
                    </strong>{' '}
                    – Die betroffene Person hat ihre Einwilligung in die Verarbeitung der sie betreffenden
                    personenbezogenen Daten für einen oder mehrere bestimmte Zwecke gegeben.
                  </li>
                  <li>
                    <strong className="text-[#2F4858]">
                      Vertragserfüllung (Art. 6 Abs. 1 S. 1 lit. b DSGVO)
                    </strong>{' '}
                    – Die Verarbeitung ist für die Erfüllung eines Vertrags oder zur Durchführung vorvertraglicher
                    Maßnahmen erforderlich.
                  </li>
                  <li>
                    <strong className="text-[#2F4858]">
                      Berechtigte Interessen (Art. 6 Abs. 1 S. 1 lit. f DSGVO)
                    </strong>{' '}
                    – Die Verarbeitung ist zur Wahrung der berechtigten Interessen des Verantwortlichen oder eines
                    Dritten erforderlich.
                  </li>
                </ul>
              </div>
            </article>

            {/* Server Log Files */}
            <article className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#2F4858]">
                  <i className="ri-server-line text-2xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-[#2F4858] ml-4">4. Server-Logdateien</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 text-[15px]">
                <p>
                  Der Provider der Seiten erhebt und speichert automatisch Informationen in sogenannten
                  Server-Logdateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Browsertyp und Browserversion</li>
                  <li>Verwendetes Betriebssystem</li>
                  <li>Referrer URL</li>
                  <li>Hostname des zugreifenden Rechners</li>
                  <li>Uhrzeit der Serveranfrage</li>
                  <li>IP-Adresse</li>
                </ul>
                <p>
                  Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen. Die Erfassung
                  dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der Websitebetreiber hat ein
                  berechtigtes Interesse an der technisch fehlerfreien Darstellung und der Optimierung seiner
                  Website.
                </p>
              </div>
            </article>

            {/* Contact Form */}
            <article className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#FF6B35]">
                  <i className="ri-mail-send-line text-2xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-[#2F4858] ml-4">5. Kontaktformular</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 text-[15px]">
                <p>
                  Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem
                  Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der
                  Anfrage und für den Fall von Anschlussfragen bei uns gespeichert.
                </p>
                <p>
                  Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO, sofern Ihre
                  Anfrage mit der Erfüllung eines Vertrags zusammenhängt oder zur Durchführung vorvertraglicher
                  Maßnahmen erforderlich ist. In allen übrigen Fällen beruht die Verarbeitung auf unserem berechtigten
                  Interesse an der effektiven Bearbeitung der an uns gerichteten Anfragen (Art. 6 Abs. 1 lit. f DSGVO).
                </p>
                <p>
                  Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie uns zur Löschung
                  auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die Datenspeicherung
                  entfällt. Zwingende gesetzliche Bestimmungen – insbesondere Aufbewahrungsfristen – bleiben
                  unberührt.
                </p>
              </div>
            </article>

            {/* Your Rights */}
            <article className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#FF6B35]">
                  <i className="ri-user-settings-line text-2xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-[#2F4858] ml-4">6. Ihre Rechte</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 text-[15px]">
                <p>Sie haben gegenüber uns folgende Rechte hinsichtlich der Sie betreffenden personenbezogenen Daten:</p>
                <ul className="space-y-3 ml-2">
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#2F4858] flex-shrink-0 mt-0.5 mr-3">
                      <i className="ri-check-line text-xs text-white"></i>
                    </div>
                    <span>
                      <strong className="text-[#2F4858]">Recht auf Auskunft</strong> – Sie können Auskunft über Ihre
                      von uns verarbeiteten personenbezogenen Daten verlangen (Art. 15 DSGVO).
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#2F4858] flex-shrink-0 mt-0.5 mr-3">
                      <i className="ri-check-line text-xs text-white"></i>
                    </div>
                    <span>
                      <strong className="text-[#2F4858]">Recht auf Berichtigung</strong> – Sie können die Berichtigung
                      unrichtiger oder die Vervollständigung Ihrer bei uns gespeicherten Daten verlangen (Art. 16 DSGVO).
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#2F4858] flex-shrink-0 mt-0.5 mr-3">
                      <i className="ri-check-line text-xs text-white"></i>
                    </div>
                    <span>
                      <strong className="text-[#2F4858]">Recht auf Löschung</strong> – Sie können die Löschung Ihrer
                      bei uns gespeicherten personenbezogenen Daten verlangen (Art. 17 DSGVO).
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#2F4858] flex-shrink-0 mt-0.5 mr-3">
                      <i className="ri-check-line text-xs text-white"></i>
                    </div>
                    <span>
                      <strong className="text-[#2F4858]">Recht auf Einschränkung</strong> – Sie können die Einschränkung
                      der Verarbeitung Ihrer personenbezogenen Daten verlangen (Art. 18 DSGVO).
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#2F4858] flex-shrink-0 mt-0.5 mr-3">
                      <i className="ri-check-line text-xs text-white"></i>
                    </div>
                    <span>
                      <strong className="text-[#2F4858]">Recht auf Datenübertragbarkeit</strong> – Sie können verlangen,
                      dass wir Ihnen Ihre Daten in einem gängigen, maschinenlesbaren Format übermitteln (Art. 20 DSGVO).
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#2F4858] flex-shrink-0 mt-0.5 mr-3">
                      <i className="ri-check-line text-xs text-white"></i>
                    </div>
                    <span>
                      <strong className="text-[#2F4858]">Widerspruchsrecht</strong> – Sie können der Verarbeitung Ihrer
                      personenbezogenen Daten jederzeit widersprechen (Art. 21 DSGVO).
                    </span>
                  </li>
                </ul>
              </div>
            </article>

            {/* SSL/TLS */}
            <article className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#FF6B35]">
                  <i className="ri-lock-line text-2xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-[#2F4858] ml-4">7. SSL- bzw. TLS-Verschlüsselung</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 text-[15px]">
                <p>
                  Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, wie
                  zum Beispiel Anfragen, die Sie an uns als Seitenbetreiber senden, eine SSL- bzw. TLS-Verschlüsselung.
                  Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von
                  &quot;http://&quot; auf &quot;https://&quot; wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.
                </p>
                <p>
                  Wenn die SSL- bzw. TLS-Verschlüsselung aktiviert ist, können die Daten, die Sie an uns übermitteln,
                  nicht von Dritten mitgelesen werden.
                </p>
              </div>
            </article>

            {/* Supervisory Authority */}
            <article className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#2F4858]">
                  <i className="ri-government-line text-2xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-[#2F4858] ml-4">8. Beschwerderecht bei der Aufsichtsbehörde</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 text-[15px]">
                <p>
                  Unbeschadet eines anderweitigen verwaltungsrechtlichen oder gerichtlichen Rechtsbehelfs steht Ihnen
                  das Recht auf Beschwerde bei einer Aufsichtsbehörde zu, wenn Sie der Ansicht sind, dass die
                  Verarbeitung der Sie betreffenden personenbezogenen Daten gegen die DSGVO verstößt.
                </p>
                <p>Die zuständige Aufsichtsbehörde ist:</p>
                <div className="bg-gray-50 rounded-lg p-4 mt-2">
                  <p className="font-semibold text-[#2F4858]">
                    Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen
                  </p>
                  <p>Kavalleriestr. 2-4</p>
                  <p>40213 Düsseldorf</p>
                  <p className="mt-2">
                    <a
                      href="https://www.ldi.nrw.de"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-[#FF6B35] hover:underline cursor-pointer"
                    >
                      www.ldi.nrw.de
                    </a>
                  </p>
                </div>
              </div>
            </article>

            {/* Changes */}
            <article className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#2F4858]">
                  <i className="ri-refresh-line text-2xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-[#2F4858] ml-4">9. Änderung dieser Datenschutzerklärung</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 text-[15px]">
                <p>
                  Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen
                  Anforderungen entspricht oder um Änderungen unserer Leistungen in der Datenschutzerklärung umzusetzen.
                  Für Ihren erneuten Besuch gilt dann die neue Datenschutzerklärung.
                </p>
                <p className="text-sm text-gray-500 mt-4">Stand: Januar 2025</p>
              </div>
            </article>
          </div>

          {/* Back to Home */}
          <div className="mt-16 text-center">
            <Link
              to="/"
              className="inline-flex items-center px-8 py-4 bg-[#2F4858] text-white font-semibold rounded-full hover:bg-[#3a5a6e] transition-all hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2 text-lg"></i>
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#f5f0eb] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-600">
            <p>© 2025 Hommen Consulting GmbH. Alle Rechte vorbehalten.</p>
            <div className="mt-4 md:mt-0 flex items-center space-x-6">
              <Link to="/impressum" className="hover:text-[#FF6B35] transition-colors cursor-pointer">
                Impressum
              </Link>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="hover:text-[#FF6B35] transition-colors cursor-pointer"
              >
                Web Design
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
